import CreateContentModal from '@/components/CreateContentModal';
import { useUserProfile } from '@/contexts/SubscriptionContext';
import { useRevisionContent } from '@/hooks/useRevisionContent';
import { getMonthStartUTC } from '@/lib/dates';
import { supabase } from '@/lib/supabase';
import { useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import { BookOpenText, Cards, CaretRight, ChatCircleDots, FileText, Fire, Lightning, Lock, Question, Star, Trophy } from 'phosphor-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Animated, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ProgressBar, Text } from 'react-native-paper';

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 1200;

// ✅ Limites FREE (par mois) - METS À 0 POUR TESTER
const FREE_LIMITS = {
  fiches: 5,
  quiz: 5,
  flashcards: 5,
  coach: 10,
};

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo400: '#A5B4FC',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate800: '#1E293B',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  amber500: '#F59E0B',
  amber400: '#FBBF24',
  amber100: '#FEF3C7',
  emerald500: '#10B981',
  emerald400: '#34D399',
  emerald100: '#D1FAE5',
  rose500: '#F43F5E',
  rose100: '#FFE4E6',
  purple600: '#7C3AED',
};

export default function DashboardScreen() {
  const { profile, loading: profileLoading } = useUserProfile();
  const { items, loading: contentLoading } = useRevisionContent();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createIntent, setCreateIntent] = useState<'fiche' | 'quiz' | 'flashcards'>('fiche');

  const [quotas, setQuotas] = useState({
    fiches: 0,
    quiz: 0,
    flashcards: 0,
    coach: 0,
  });
  const [quotasLoading, setQuotasLoading] = useState(true);

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;
  const scaleAnim = useRef(new Animated.Value(0.95)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const floatAnim = useRef(new Animated.Value(0)).current;

  // ✅ Fonction de chargement des quotas (extraite pour réutilisation)
  const loadQuotas = useCallback(async () => {
    if (!profile?.id) return;

    try {
      const monthStart = getMonthStartUTC();

      const { count: fichesCount } = await supabase
        .from('notes')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .gte('created_at', monthStart);

      const { count: quizCount } = await supabase
        .from('quizzes')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .gte('created_at', monthStart);

      const { count: flashcardsCount } = await supabase
        .from('flashcard_sets')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .gte('created_at', monthStart);

      const { count: coachCount } = await supabase
        .from('chat_messages')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .eq('role', 'user')
        .gte('created_at', monthStart);

      setQuotas({
        fiches: fichesCount || 0,
        quiz: quizCount || 0,
        flashcards: flashcardsCount || 0,
        coach: coachCount || 0,
      });
    } catch (error) {
      console.error('Error loading quotas:', error);
    } finally {
      setQuotasLoading(false);
    }
  }, [profile?.id]);

  // ✅ Charger les quotas au montage
  useEffect(() => {
    loadQuotas();
  }, [loadQuotas]);

  // ✅ Rafraîchir les quotas quand l'utilisateur revient sur cette page
  useFocusEffect(
    useCallback(() => {
      loadQuotas();
    }, [loadQuotas])
  );

  useEffect(() => {
    if (isWeb) {
      fadeAnim.setValue(1);
      slideAnim.setValue(0);
      scaleAnim.setValue(1);
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.spring(slideAnim, {
          toValue: 0,
          friction: 8,
          tension: 40,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 40,
          useNativeDriver: true,
        }),
      ]).start();
    }

    if (!isWeb) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.02,
            duration: 1500,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1500,
            useNativeDriver: true,
          }),
        ])
      );
      pulse.start();

      const float = Animated.loop(
        Animated.sequence([
          Animated.timing(floatAnim, {
            toValue: -6,
            duration: 1500,
            useNativeDriver: true,
          }),
          Animated.timing(floatAnim, {
            toValue: 0,
            duration: 1500,
            useNativeDriver: true,
          }),
        ])
      );
      float.start();

      return () => {
        pulse.stop();
        float.stop();
      };
    }
  }, []);

  const isPremium = profile?.premium || false;
  const xp = profile?.xp || 0;
  const currentLevel = Math.floor(xp / 100) + 1;
  const xpInCurrentLevel = xp % 100;
  const xpForNextLevel = 100;
  const progress = xpInCurrentLevel / xpForNextLevel;
  const streak = profile?.streak_days || 0;
  const bestStreak = profile?.best_streak || 0;

  const isLimitReached = (type: 'fiches' | 'quiz' | 'flashcards' | 'coach') => {
    if (isPremium) return false;
    return quotas[type] >= FREE_LIMITS[type];
  };

  const limitsReachedCount = isPremium ? 0 :
    (isLimitReached('fiches') ? 1 : 0) +
    (isLimitReached('quiz') ? 1 : 0) +
    (isLimitReached('flashcards') ? 1 : 0) +
    (isLimitReached('coach') ? 1 : 0);

  const getLevelTitle = (level: number) => {
    if (level <= 2) return 'Débutant';
    if (level <= 5) return 'Apprenti';
    if (level <= 10) return 'Confirmé';
    if (level <= 20) return 'Expert';
    return 'Maître';
  };

  const lastContent = items.length > 0 ? items[0] : null;

  const getRelativeTime = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInHours < 1) return "à l'instant";
    if (diffInHours < 24) return `il y a ${diffInHours}h`;
    if (diffInDays === 1) return 'hier';
    if (diffInDays < 7) return `il y a ${diffInDays} jours`;
    return `il y a ${Math.floor(diffInDays / 7)} sem.`;
  };

  const getDaysOfWeek = () => {
    const days = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
    const today = new Date().getDay();

    return days.map((day, index) => {
      const dayIndex = index === 6 ? 0 : index + 1;
      const daysFromToday = (today - dayIndex + 7) % 7;
      const isActive = daysFromToday < streak && daysFromToday >= 0;
      const isToday = dayIndex === today;

      return { day, isActive, isToday };
    });
  };

  // ✅ Fonction pour rediriger vers le paywall
  const redirectToPaywall = () => {
    router.push('/paywall');
  };

  // ✅ Afficher l'Alert paywall - BLOCAGE IMMÉDIAT avec "Passer Premium" EN GRAS
  const showPaywallAlert = (contentType: string) => {
    const limit = contentType === 'coach' ? FREE_LIMITS.coach :
                  contentType === 'fiche' ? FREE_LIMITS.fiches :
                  contentType === 'quiz' ? FREE_LIMITS.quiz :
                  FREE_LIMITS.flashcards;
    
    const contentName = contentType === 'coach' ? 'messages Coach IA' :
                        contentType === 'fiche' ? 'fiches' :
                        contentType === 'quiz' ? 'quiz' : 'sets de flashcards';

    const message = `Tu as atteint ta limite de ${limit} ${contentName} par mois. Passe Premium pour un accès illimité !`;

    if (isWeb) {
      if (window.confirm(message + '\n\nPasser Premium ?')) {
        redirectToPaywall();
      }
    } else {
      Alert.alert(
        'Limite atteinte',
        message,
        [
          { 
            text: 'Passer Premium', 
            style: 'default',
            isPreferred: true,  // ← BOUTON EN GRAS sur iOS
            onPress: redirectToPaywall 
          },
          { text: 'Plus tard', style: 'cancel' },
        ]
      );
    }
  };

  // ✅ BLOCAGE IMMÉDIAT AU CLIC si limite atteinte
  const handleCoachPress = () => {
    if (isLimitReached('coach')) {
      showPaywallAlert('coach');
      return;
    }
    router.push('/(tabs)/coach');
  };

  // ✅ BLOCAGE IMMÉDIAT AU CLIC si limite atteinte
  const handleCreateContent = (type: 'fiche' | 'quiz' | 'flashcards') => {
    if (type === 'fiche' && isLimitReached('fiches')) {
      showPaywallAlert('fiche');
      return;
    }
    if (type === 'quiz' && isLimitReached('quiz')) {
      showPaywallAlert('quiz');
      return;
    }
    if (type === 'flashcards' && isLimitReached('flashcards')) {
      showPaywallAlert('flashcards');
      return;
    }
    setCreateIntent(type);
    setShowCreateModal(true);
  };

  const handleContentPress = (item: any) => {
    switch (item.type) {
      case 'fiche': router.push(`/notes/${item.id}`); break;
      case 'quiz': router.push(`/quiz/${item.id}`); break;
      case 'flashcards': router.push(`/flashcards/${item.id}`); break;
    }
  };

  const handleReviserPress = () => router.push('/(tabs)/revise');

  const handlePremiumPress = () => {
    router.push('/paywall');
  };

  const QuotaRow = ({
    icon,
    label,
    used,
    max
  }: {
    icon: React.ReactNode;
    label: string;
    used: number;
    max: number;
  }) => {
    const isReached = used >= max;
    const percentage = Math.min((used / max) * 100, 100);

    return (
      <View style={styles.quotaRow}>
        <View style={styles.quotaRowIcon}>{icon}</View>
        <Text style={styles.quotaRowLabel}>{label}</Text>
        <View style={styles.quotaRowBarContainer}>
          <View style={styles.quotaRowBarBackground}>
            <View
              style={[
                styles.quotaRowBarFill,
                { width: `${percentage}%` },
                isReached && styles.quotaRowBarFull
              ]}
            />
          </View>
        </View>
        <Text style={[
          styles.quotaRowCount,
          isReached && styles.quotaRowCountFull
        ]}>
          {used}/{max}
        </Text>
        {isReached && (
          <View style={styles.quotaRowLock}>
            <Lock size={14} color={colors.rose500} weight="duotone" />
          </View>
        )}
      </View>
    );
  };

  if (profileLoading || contentLoading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <Animated.View style={{ opacity: fadeAnim }}>
          <Text style={styles.loadingText}>Chargement...</Text>
        </Animated.View>
      </View>
    );
  }

  // ✅ Pré-calculer les locks AVANT le render
  const coachLocked = isLimitReached('coach');
  const fichesLocked = isLimitReached('fiches');
  const quizLocked = isLimitReached('quiz');
  const flashcardsLocked = isLimitReached('flashcards');

  return (
    <View style={styles.container}>
      {/* ========== HEADER FIXE - centré sur web ========== */}
      <View style={styles.headerWrapper}>
        <LinearGradient
          colors={[colors.indigo500, colors.indigo600, colors.indigo700]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.heroHeader}
        >
          <View style={styles.heroContent}>
            <View style={styles.mascotContainer}>
              <Animated.Image 
                source={require('../../assets/images/ernest.png')} 
                style={[
                  styles.mascotImage,
                  !isWeb && { transform: [{ translateY: floatAnim }] }
                ]}
              />
            </View>

            <View style={styles.heroLeft}>
              <Text style={styles.greeting}>
                Salut {profile?.full_name || 'toi'} !
              </Text>
              <Text style={styles.subGreeting}>Prêt à réviser ?</Text>
            </View>

            <View style={styles.heroBadges}>
              <View style={styles.xpBadge}>
                <Lightning size={16} color={colors.amber400} weight="fill" />
                <Text style={styles.xpBadgeText}>{xp} XP</Text>
              </View>
              {!isPremium && (
                <View style={styles.freeBadge}>
                  <Text style={styles.freeBadgeText}>FREE</Text>
                </View>
              )}
            </View>
          </View>
        </LinearGradient>
      </View>

      {/* ========== CONTENU SCROLLABLE ========== */}
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        <Animated.View style={[
          styles.innerContainer,
          !isWeb && {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }, { scale: scaleAnim }]
          }
        ]}>

          {/* ========== 1. CARTE NIVEAU ========== */}
          <View style={styles.section}>
            <View style={styles.progressCard}>
              <View style={styles.progressTop}>
                <LinearGradient
                  colors={[colors.indigo600, colors.purple600]}
                  style={styles.levelCircle}
                >
                  <Text style={styles.levelNumber}>{currentLevel}</Text>
                </LinearGradient>
                <View style={styles.progressInfo}>
                  <Text style={styles.levelTitle}>
                    Niveau {currentLevel} • {getLevelTitle(currentLevel)}
                  </Text>
                  <Text style={styles.xpText}>{xpInCurrentLevel} / {xpForNextLevel} XP</Text>
                </View>
              </View>
              <View style={styles.progressBarContainer}>
                <ProgressBar
                  progress={progress}
                  color={colors.indigo600}
                  style={styles.progressBar}
                />
              </View>
              <Text style={styles.progressHint}>
                +{xpForNextLevel - xpInCurrentLevel} XP pour passer niveau {currentLevel + 1}
              </Text>
            </View>
          </View>

          {/* ========== 2. QUE VEUX-TU FAIRE ? ========== */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Que veux-tu faire ?</Text>

            <View style={styles.actionsGrid}>
              <View style={styles.actionsRow}>
                <TouchableOpacity
                  style={styles.actionCard}
                  onPress={handleCoachPress}
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={[colors.indigo100, colors.indigo50]}
                    style={styles.actionIconGradient}
                  >
                    <ChatCircleDots size={28} color={colors.indigo600} weight="duotone" />
                  </LinearGradient>
                  <Text style={styles.actionTitle}>Coach IA</Text>
                  <Text style={styles.actionSubtitle}>Poser une question</Text>
                  {coachLocked && (
                    <View style={styles.actionLocked}>
                      <Lock size={16} color={colors.white} weight="fill" />
                    </View>
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.actionCard}
                  onPress={() => handleCreateContent('fiche')}
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={[colors.indigo100, colors.indigo50]}
                    style={styles.actionIconGradient}
                  >
                    <FileText size={28} color={colors.indigo600} weight="duotone" />
                  </LinearGradient>
                  <Text style={styles.actionTitle}>Fiche</Text>
                  <Text style={styles.actionSubtitle}>Depuis ton cours</Text>
                  {fichesLocked && (
                    <View style={styles.actionLocked}>
                      <Lock size={16} color={colors.white} weight="fill" />
                    </View>
                  )}
                </TouchableOpacity>
              </View>

              <View style={styles.actionsRow}>
                <TouchableOpacity
                  style={styles.actionCard}
                  onPress={() => handleCreateContent('quiz')}
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={[colors.amber100, '#FFFBEB']}
                    style={styles.actionIconGradient}
                  >
                    <Question size={28} color={colors.amber500} weight="duotone" />
                  </LinearGradient>
                  <Text style={styles.actionTitle}>Quiz</Text>
                  <Text style={styles.actionSubtitle}>Depuis ton cours</Text>
                  {quizLocked && (
                    <View style={styles.actionLocked}>
                      <Lock size={16} color={colors.white} weight="fill" />
                    </View>
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.actionCard}
                  onPress={() => handleCreateContent('flashcards')}
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={[colors.emerald100, '#ECFDF5']}
                    style={styles.actionIconGradient}
                  >
                    <Cards size={28} color={colors.emerald500} weight="duotone" />
                  </LinearGradient>
                  <Text style={styles.actionTitle}>Flashcards</Text>
                  <Text style={styles.actionSubtitle}>Depuis ton cours</Text>
                  {flashcardsLocked && (
                    <View style={styles.actionLocked}>
                      <Lock size={16} color={colors.white} weight="fill" />
                    </View>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>

          {/* ========== 3. QUOTA DU MOIS ========== */}
          {!isPremium && (
            <View style={styles.section}>
              <View style={styles.quotaCard}>
                <View style={styles.quotaHeader}>
                  <View style={styles.quotaIconContainer}>
                    <BookOpenText size={24} color={colors.indigo600} weight="duotone" />
                  </View>
                  <View style={styles.quotaInfo}>
                    <Text style={styles.quotaTitle}>Quota du mois</Text>
                    <Text style={styles.quotaSubtitle}>
                      {limitsReachedCount === 0
                        ? 'Tout est disponible !'
                        : limitsReachedCount === 4
                        ? 'Toutes les limites atteintes'
                        : `${limitsReachedCount} limite${limitsReachedCount > 1 ? 's' : ''} atteinte${limitsReachedCount > 1 ? 's' : ''}`
                      }
                    </Text>
                  </View>
                </View>

                <View style={styles.quotaRows}>
                  <QuotaRow
                    icon={<FileText size={18} color={colors.indigo600} weight="duotone" />}
                    label="Fiches"
                    used={quotas.fiches}
                    max={FREE_LIMITS.fiches}
                  />
                  <QuotaRow
                    icon={<Question size={18} color={colors.amber500} weight="duotone" />}
                    label="Quiz"
                    used={quotas.quiz}
                    max={FREE_LIMITS.quiz}
                  />
                  <QuotaRow
                    icon={<Cards size={18} color={colors.emerald500} weight="duotone" />}
                    label="Flashcards"
                    used={quotas.flashcards}
                    max={FREE_LIMITS.flashcards}
                  />
                  <QuotaRow
                    icon={<ChatCircleDots size={18} color={colors.purple600} weight="duotone" />}
                    label="Coach IA"
                    used={quotas.coach}
                    max={FREE_LIMITS.coach}
                  />
                </View>
              </View>
            </View>
          )}

          {/* ========== 4. BANNIÈRE PREMIUM (simplifiée) ========== */}
          {!isPremium && (
            <View style={styles.section}>
              <Animated.View style={!isWeb ? { transform: [{ scale: pulseAnim }] } : undefined}>
                <TouchableOpacity
                  activeOpacity={0.9}
                  onPress={handlePremiumPress}
                >
                  <LinearGradient
                    colors={[colors.indigo600, colors.purple600]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.premiumBanner}
                  >
                    <View style={styles.premiumGlow} />

                    <View style={styles.premiumBadge}>
                      <Text style={styles.premiumBadgeText}>PREMIUM</Text>
                    </View>

                    <View style={styles.premiumContent}>
                      <View style={styles.premiumLeft}>
                        <Text style={styles.premiumTitle}>
                          Révise en <Text style={styles.premiumHighlight}>illimité</Text>
                        </Text>
                        <View style={styles.premiumFeatures}>
                          <Text style={styles.premiumFeature}>✓ Fiches illimitées</Text>
                          <Text style={styles.premiumFeature}>✓ Quiz & Flashcards ∞</Text>
                          <Text style={styles.premiumFeature}>✓ Coach IA illimité</Text>
                          <Text style={styles.premiumFeature}>✓ Contenus créés par un prof</Text>
                        </View>
                      </View>
                    </View>

                    <View style={styles.premiumCTA}>
                      <Text style={styles.premiumCTAText}>Découvrir les offres</Text>
                    </View>

                    <Text style={styles.premiumDisclaimer}>Annulable à tout moment</Text>
                  </LinearGradient>
                </TouchableOpacity>
              </Animated.View>
            </View>
          )}

          {/* ========== 5. CETTE SEMAINE ========== */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Cette semaine</Text>
              {streak > 0 && (
                <View style={styles.streakBadgeSmall}>
                  <Fire size={16} color={colors.amber500} weight="fill" />
                  <Text style={styles.streakBadgeText}>{streak}j</Text>
                </View>
              )}
            </View>

            <View style={styles.weekCard}>
              <View style={styles.weekDays}>
                {getDaysOfWeek().map((item, index) => (
                  <View key={index} style={styles.dayContainer}>
                    <View style={[
                      styles.dayCircle,
                      item.isActive && styles.dayCircleActive,
                      item.isToday && !item.isActive && styles.dayCircleToday,
                    ]}>
                      {item.isActive ? (
                        <Fire size={18} color={colors.white} weight="fill" />
                      ) : item.isToday ? (
                        <View style={styles.dayTodayDot} />
                      ) : null}
                    </View>
                    <Text style={[
                      styles.dayLabel,
                      item.isActive && styles.dayLabelActive,
                      item.isToday && styles.dayLabelToday,
                    ]}>{item.day}</Text>
                  </View>
                ))}
              </View>

              <View style={styles.weekMessageWrapper}>
                <LinearGradient
                  colors={streak > 0 ? [colors.amber100, '#FFFBEB'] : [colors.slate100, colors.slate50]}
                  style={styles.weekMessage}
                >
                  {streak > 0 ? (
                    <Fire size={22} color={colors.amber500} weight="fill" />
                  ) : (
                    <Star size={22} color={colors.slate400} weight="duotone" />
                  )}
                  <Text style={styles.weekMessageText}>
                    {streak === 0 && "Révise aujourd'hui pour lancer ta série !"}
                    {streak === 1 && "Super début ! Continue demain !"}
                    {streak >= 2 && streak < 7 && `${streak} jours d'affilée, continue !`}
                    {streak >= 7 && `Incroyable ! ${streak} jours ! 🏆`}
                  </Text>
                </LinearGradient>
              </View>
            </View>
          </View>

          {/* ========== 6. DERNIÈRE ACTIVITÉ ========== */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Dernière activité</Text>
              <TouchableOpacity onPress={handleReviserPress} style={styles.seeAllButtonContainer}>
                <Text style={styles.seeAllButton}>Tout voir</Text>
                <CaretRight size={16} color={colors.indigo600} weight="bold" />
              </TouchableOpacity>
            </View>

            {lastContent ? (
              <TouchableOpacity
                activeOpacity={0.8}
                onPress={() => handleContentPress(lastContent)}
              >
                <View style={styles.activityCard}>
                  <LinearGradient
                    colors={
                      lastContent.type === 'fiche' ? [colors.indigo100, colors.indigo50] :
                      lastContent.type === 'quiz' ? [colors.amber100, '#FFFBEB'] :
                      [colors.emerald100, '#ECFDF5']
                    }
                    style={styles.activityIcon}
                  >
                    {lastContent.type === 'fiche' && <FileText size={24} color={colors.indigo600} weight="duotone" />}
                    {lastContent.type === 'quiz' && <Question size={24} color={colors.amber500} weight="duotone" />}
                    {lastContent.type === 'flashcards' && <Cards size={24} color={colors.emerald500} weight="duotone" />}
                  </LinearGradient>
                  <View style={styles.activityInfo}>
                    <Text style={styles.activityTitle} numberOfLines={1}>
                      {lastContent.title || 'Sans titre'}
                    </Text>
                    <Text style={styles.activityMeta}>
                      {lastContent.type === 'fiche' ? 'Fiche' : lastContent.type === 'quiz' ? 'Quiz' : 'Flashcards'} • {getRelativeTime(lastContent.created_at)}
                    </Text>
                  </View>
                  <View style={styles.activityChevron}>
                    <CaretRight size={20} color={colors.slate500} weight="bold" />
                  </View>
                </View>
              </TouchableOpacity>
            ) : (
              <View style={styles.emptyCard}>
                <BookOpenText size={48} color={colors.slate400} weight="duotone" />
                <Text style={styles.emptyText}>Aucune activité récente</Text>
                <Text style={styles.emptyHint}>Crée ta première fiche pour commencer !</Text>
              </View>
            )}
          </View>

          {/* ========== 7. BEST STREAK ========== */}
          {bestStreak > 0 && (
            <View style={styles.section}>
              <LinearGradient
                colors={[colors.indigo600, colors.purple600]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.bestStreakCard}
              >
                <View style={styles.bestStreakGlow} />

                <View style={styles.bestStreakIconContainer}>
                  <Trophy size={28} color={colors.amber500} weight="fill" />
                </View>

                <View style={styles.bestStreakInfo}>
                  <View style={styles.bestStreakBadge}>
                    <Text style={styles.bestStreakBadgeText}>🏆 RECORD</Text>
                  </View>
                  <Text style={styles.bestStreakValue}>{bestStreak} jours</Text>
                </View>

                {streak > 0 && streak < bestStreak && (
                  <View style={styles.bestStreakProgress}>
                    <Text style={styles.bestStreakProgressText}>
                      Plus que {bestStreak - streak} !
                    </Text>
                  </View>
                )}

                {streak >= bestStreak && streak > 0 && (
                  <View style={styles.bestStreakProgress}>
                    <Text style={styles.bestStreakProgressText}>
                      🔥 En cours !
                    </Text>
                  </View>
                )}
              </LinearGradient>
            </View>
          )}

          <View style={{ height: 120 }} />
        </Animated.View>
      </ScrollView>

      <CreateContentModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        initialIntent={createIntent}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  headerWrapper: {
    backgroundColor: colors.slate50,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  heroHeader: {
    paddingTop: isWeb ? 40 : 60,
    paddingBottom: 28,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 32,
    borderBottomRightRadius: 32,
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    ...(isWeb && {
      minHeight: 160,
    }),
  },
  heroContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  mascotContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  mascotImage: {
    width: 40,
    height: 40,
    resizeMode: 'contain',
  },
  heroLeft: {
    flex: 1,
  },
  greeting: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 6,
    letterSpacing: -0.5,
  },
  streakContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  subGreeting: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '500',
  },
  heroBadges: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
  },
  xpBadge: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  xpBadgeText: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.white,
  },
  freeBadge: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  freeBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.white,
    letterSpacing: 0.5,
  },
  scrollContainer: {
    flex: 1,
  },
  contentContainer: {
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  innerContainer: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    alignSelf: 'center',
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: colors.slate500,
    fontSize: 16,
  },
  progressCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 20,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 4,
  },
  progressTop: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  levelCircle: {
    width: 52,
    height: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  levelNumber: {
    fontSize: 24,
    fontWeight: '800',
    color: colors.white,
  },
  progressInfo: {
    flex: 1,
  },
  levelTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.slate900,
  },
  xpText: {
    fontSize: 14,
    color: colors.slate500,
    marginTop: 2,
  },
  progressBarContainer: {
    marginBottom: 10,
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.slate200,
  },
  progressHint: {
    fontSize: 13,
    color: colors.slate500,
    textAlign: 'center',
  },
  section: {
    paddingHorizontal: 20,
    marginTop: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    letterSpacing: -0.3,
  },
  seeAllButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  seeAllButton: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.indigo600,
  },
  quotaCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 18,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  quotaHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  quotaIconContainer: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: colors.indigo50,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  quotaInfo: {
    flex: 1,
  },
  quotaTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.slate900,
  },
  quotaSubtitle: {
    fontSize: 13,
    color: colors.slate500,
    marginTop: 2,
  },
  quotaRows: {
    gap: 10,
  },
  quotaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  quotaRowIcon: {
    width: 24,
    alignItems: 'center',
  },
  quotaRowLabel: {
    fontSize: 14,
    color: colors.slate600,
    width: 75,
  },
  quotaRowBarContainer: {
    flex: 1,
  },
  quotaRowBarBackground: {
    height: 6,
    backgroundColor: colors.slate200,
    borderRadius: 3,
    overflow: 'hidden',
  },
  quotaRowBarFill: {
    height: '100%',
    backgroundColor: colors.indigo600,
    borderRadius: 3,
  },
  quotaRowBarFull: {
    backgroundColor: colors.rose500,
  },
  quotaRowCount: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.slate500,
    width: 36,
    textAlign: 'right',
  },
  quotaRowCountFull: {
    color: colors.rose500,
  },
  quotaRowLock: {
    width: 20,
    alignItems: 'center',
  },
  premiumBanner: {
    borderRadius: 24,
    padding: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  premiumGlow: {
    position: 'absolute',
    top: -80,
    right: -80,
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  premiumContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  premiumLeft: {
    flex: 1,
  },
  premiumTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 10,
  },
  premiumHighlight: {
    fontWeight: '800',
    color: colors.amber400,
  },
  premiumFeatures: {
    gap: 4,
  },
  premiumFeature: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.95)',
    fontWeight: '500',
  },
  premiumBadge: {
    backgroundColor: colors.amber100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    position: 'absolute',
    top: 20,
    right: 20,
  },
  premiumBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.amber500,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  premiumCTA: {
    backgroundColor: colors.white,
    borderRadius: 14,
    padding: 14,
    marginTop: 18,
    alignItems: 'center',
  },
  premiumCTAText: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.indigo600,
  },
  premiumDisclaimer: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.7)',
    textAlign: 'center',
    marginTop: 12,
  },
  actionsGrid: {
    gap: 14,
    marginTop: 16,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 14,
  },
  actionCard: {
    flex: 1,
    backgroundColor: colors.white,
    borderRadius: 24,
    padding: 20,
    alignItems: 'center',
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
    position: 'relative',
  },
  actionIconGradient: {
    width: 60,
    height: 60,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 4,
  },
  actionSubtitle: {
    fontSize: 13,
    color: colors.slate500,
  },
  actionLocked: {
    position: 'absolute',
    top: 12,
    right: 12,
    backgroundColor: colors.slate800,
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activityCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 16,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  activityIcon: {
    width: 52,
    height: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  activityInfo: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
    marginBottom: 4,
  },
  activityMeta: {
    fontSize: 13,
    color: colors.slate500,
  },
  activityChevron: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 32,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.slate200,
    borderStyle: 'dashed',
  },
  emptyText: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.slate900,
    marginTop: 12,
    marginBottom: 4,
  },
  emptyHint: {
    fontSize: 14,
    color: colors.slate500,
  },
  weekCard: {
    backgroundColor: colors.white,
    borderRadius: 24,
    padding: 20,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  streakBadgeSmall: {
    backgroundColor: colors.amber100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  streakBadgeText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.amber500,
  },
  weekDays: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  dayContainer: {
    alignItems: 'center',
    gap: 8,
  },
  dayCircle: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayCircleActive: {
    backgroundColor: colors.amber500,
  },
  dayCircleToday: {
    borderWidth: 2,
    borderColor: colors.indigo600,
    backgroundColor: colors.white,
  },
  dayTodayDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.indigo600,
  },
  dayLabel: {
    fontSize: 13,
    color: colors.slate400,
    fontWeight: '500',
  },
  dayLabelActive: {
    color: colors.amber500,
    fontWeight: '600',
  },
  dayLabelToday: {
    color: colors.indigo600,
    fontWeight: '700',
  },
  weekMessageWrapper: {
    width: '100%',
    alignItems: 'center',
  },
  weekMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 14,
    gap: 10,
    width: '100%',
  },
  weekMessageText: {
    fontSize: 15,
    color: colors.slate900,
    fontWeight: '600',
    textAlign: 'center',
  },
  bestStreakCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 20,
    padding: 18,
    position: 'relative',
    overflow: 'hidden',
    shadowColor: colors.indigo700,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 8,
  },
  bestStreakGlow: {
    position: 'absolute',
    top: -30,
    right: -30,
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  bestStreakIconContainer: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  bestStreakInfo: {
    flex: 1,
    marginLeft: 14,
  },
  bestStreakBadge: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginBottom: 4,
  },
  bestStreakBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.white,
    letterSpacing: 0.5,
  },
  bestStreakValue: {
    fontSize: 24,
    fontWeight: '800',
    color: colors.white,
  },
  bestStreakProgress: {
    backgroundColor: 'rgba(255,255,255,0.25)',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 12,
  },
  bestStreakProgressText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.white,
  },
});
